import * as cornerstone from "@cornerstonejs/core";
import { getRenderingEngine } from "@cornerstonejs/core";
import { RequestType } from "@cornerstonejs/core/enums";
import * as cornerstonetools from "@cornerstonejs/tools";
import {
  ToolGroupManager
} from "@cornerstonejs/tools";
import {
  createStackImageSynchronizer,
  createVOISynchronizer,
} from "@cornerstonejs/tools/synchronizers";
import registerWebImageLoader from "./RegisterWebImageLoader";
import dicomImageLoader from "@cornerstonejs/dicom-image-loader";
import { stackPrefetch } from "@cornerstonejs/tools/utilities";
import { isMobile } from "react-device-detect";
import { MouseBindings } from "@cornerstonejs/tools/enums";
import SeriesStackScrollMouseWheelTool from "./SeriesStackScrollMouseWheelTool";

const configuration = {
  maxImagesToPrefetch: Infinity,
  preserveExistingPool: true,
};

const setSeriesStackScrollConfig = (toolGroupManager, seriesScrollCallback) => {
  const seriesStackScrollConfig = toolGroupManager.getToolConfiguration(SeriesStackScrollMouseWheelTool.toolName);
  const seriesStackScrollMergedConfig = { ...seriesStackScrollConfig, seriesScrollCallback };
  toolGroupManager.setToolConfiguration(SeriesStackScrollMouseWheelTool.toolName,seriesStackScrollMergedConfig, true);
}

export const initialize = async (seriesScrollCallback) => {
  if (!cornerstone.isCornerstoneInitialized()) {
    // console.log("in initialize");
    // Set WebGL configuration before initializing Cornerstone
    cornerstone.setConfiguration({
      rendering: {
        webgl: {
          preserveDrawingBuffer: true,
        },
      },
    });
    cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(
      RequestType.Prefetch,
      5
    );
    cornerstone.requestPoolManager.setMaxSimultaneousRequests(
      RequestType.Prefetch,
      5
    );

    registerWebImageLoader();

    let maxWebWorkers = 1;

    if (navigator.hardwareConcurrency) {
      maxWebWorkers = Math.min(navigator.hardwareConcurrency, 7);
    }
    const webWorkerTaskPath =
      window.location.origin + process.env.REACT_APP_CONVOLVE_TASK_PATH;
    dicomImageLoader.init({
      maxWebWorkers: maxWebWorkers,
      startWebWorkersOnDemand: false,
      webWorkerTaskPaths: [webWorkerTaskPath],
      beforeSend: function (xhr) {
        xhr.withCredentials = true;
      },
    });
    cornerstone.init();
    if(isMobile) {
      cornerstone.setUseCPURendering(true);
    } else {
      cornerstone.setUseCPURendering(false);
    }
    cornerstonetools.init();
    stackPrefetch.setConfiguration(configuration);
    cornerstone.cache.setMaxCacheSize(1024 * 1024 * 1024 * 3);
    cornerstonetools.ReferenceLinesTool.isAnnotation = false;
    cornerstonetools.addTool(cornerstonetools.StackScrollTool);
    cornerstonetools.addTool(SeriesStackScrollMouseWheelTool);
    cornerstonetools.addTool(cornerstonetools.WindowLevelTool);
    cornerstonetools.addTool(cornerstonetools.ZoomTool);
    cornerstonetools.addTool(cornerstonetools.PanTool);
    cornerstonetools.addTool(cornerstonetools.LengthTool);
    cornerstonetools.addTool(cornerstonetools.BidirectionalTool);
    cornerstonetools.addTool(cornerstonetools.EllipticalROITool);
    cornerstonetools.addTool(cornerstonetools.ProbeTool);
    cornerstonetools.addTool(cornerstonetools.RectangleROITool);
    cornerstonetools.addTool(cornerstonetools.ArrowAnnotateTool);
    cornerstonetools.addTool(cornerstonetools.ReferenceLinesTool);
    cornerstonetools.addTool(cornerstonetools.CrosshairsTool);
    cornerstonetools.addTool(cornerstonetools.AngleTool);
    cornerstonetools.addTool(cornerstonetools.CobbAngleTool);
    cornerstonetools.addTool(cornerstonetools.MagnifyTool);
    cornerstonetools.addTool(cornerstonetools.PlanarFreehandROITool);
    cornerstonetools.addTool(cornerstonetools.CircleROITool);
    const toolGroupManager = addToolToToolGroupManager();
    setSeriesStackScrollConfig(toolGroupManager, seriesScrollCallback);
  }
};

export const getRenderingEngineLocal = async (RENDERING_ENGINE_ID) => {
  if (!cornerstone.isCornerstoneInitialized()) {
    await initialize();
  }

  let renderingEngine = getRenderingEngine(RENDERING_ENGINE_ID);

  if (renderingEngine) {
    return renderingEngine;
  }

  if (!renderingEngine || renderingEngine.hasBeenDestroyed) {
    renderingEngine = new cornerstone.RenderingEngine(RENDERING_ENGINE_ID);
  }

  return renderingEngine;
};

export const getAugToolGroup = () => {
  let toolGroupManager = ToolGroupManager.getToolGroup(process.env.REACT_APP_TOOL_GROUP_ID);
  return toolGroupManager;
};

export const getToolGroupManager = (TOOL_GROUP_ID) => {
  // cornerstonetools.init();

  let toolGroupManager = ToolGroupManager.getToolGroup(TOOL_GROUP_ID);
  if (toolGroupManager) {
    return toolGroupManager;
  }

  if (!toolGroupManager) {
    toolGroupManager = ToolGroupManager.createToolGroup(TOOL_GROUP_ID);
  }

  toolGroupManager.addTool(SeriesStackScrollMouseWheelTool.toolName);
  toolGroupManager.setToolActive(SeriesStackScrollMouseWheelTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Wheel,
      },
    ],
  });
  
  toolGroupManager.addTool(cornerstonetools.ZoomTool.toolName);
  toolGroupManager.setToolActive(
    cornerstonetools.ZoomTool.toolName,
    {
      bindings: [{
        mouseButton: MouseBindings.Secondary, // Left Click
      }],
    }
  );
  toolGroupManager.addTool(cornerstonetools.PanTool.toolName);
  toolGroupManager.setToolActive(
    cornerstonetools.PanTool.toolName,
    {
      bindings: [{
        mouseButton: MouseBindings.Auxiliary, // Left Click
      }],
    }
  );

  toolGroupManager.addTool(cornerstonetools.ReferenceLinesTool.toolName);
  return toolGroupManager;
};

export const addToolToToolGroupManager = () => {
  const toolGroup = getToolGroupManager(process.env.REACT_APP_TOOL_GROUP_ID);
  //adding tools to toolgroup
  toolGroup.addTool(cornerstonetools.StackScrollTool.toolName);
  toolGroup.addTool(SeriesStackScrollMouseWheelTool.toolName);
  toolGroup.addTool(cornerstonetools.WindowLevelTool.toolName);
  toolGroup.addTool(cornerstonetools.ZoomTool.toolName);
  toolGroup.addTool(cornerstonetools.PanTool.toolName);
  toolGroup.addTool(cornerstonetools.LengthTool.toolName);
  toolGroup.addTool(cornerstonetools.BidirectionalTool.toolName);
  toolGroup.addTool(cornerstonetools.EllipticalROITool.toolName);
  toolGroup.addTool(cornerstonetools.ProbeTool.toolName);
  toolGroup.addTool(cornerstonetools.RectangleROITool.toolName);
  toolGroup.addTool(cornerstonetools.ArrowAnnotateTool.toolName);
  toolGroup.addTool(cornerstonetools.CrosshairsTool.toolName);
  toolGroup.addTool(cornerstonetools.AngleTool.toolName);
  toolGroup.addTool(cornerstonetools.CobbAngleTool.toolName);
  toolGroup.addTool(cornerstonetools.MagnifyTool.toolName);
  toolGroup.addTool(cornerstonetools.PlanarFreehandROITool.toolName, {
    cachedStats: true,
  });
  toolGroup.addTool(cornerstonetools.CircleROITool.toolName);
  toolGroup.addTool(cornerstonetools.ReferenceLinesTool.toolName);
  //setting active tool
  toolGroup.addTool(SeriesStackScrollMouseWheelTool.toolName);
  toolGroup.setToolActive(SeriesStackScrollMouseWheelTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Wheel,
      },
    ],
  });

  // //setting disabled tool
  // toolGroup.setToolDisabled(cornerstonetools.ReferenceLinesTool.toolName);

  //setting passive tools
  toolGroup.setToolPassive(cornerstonetools.WindowLevelTool.toolName);
  toolGroup.setToolActive(cornerstonetools.ZoomTool.toolName);
  toolGroup.setToolActive(cornerstonetools.PanTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.LengthTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.BidirectionalTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.EllipticalROITool.toolName);
  toolGroup.setToolPassive(cornerstonetools.ProbeTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.RectangleROITool.toolName);
  toolGroup.setToolPassive(cornerstonetools.ArrowAnnotateTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.AngleTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.CobbAngleTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.MagnifyTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.PlanarFreehandROITool.toolName);
  toolGroup.setToolPassive(cornerstonetools.CircleROITool.toolName);
  return toolGroup;
};

export const getSynchronizer = (SYNCHRONIZER_ID) => {
  let synchronizer =
    cornerstonetools.SynchronizerManager.getSynchronizer(SYNCHRONIZER_ID);

  if (!synchronizer) {
    synchronizer = createStackImageSynchronizer(SYNCHRONIZER_ID);
  }

  return synchronizer;
};

export const destroySynchronizer = (SYNCHRONIZER_ID) => {
  cornerstonetools.SynchronizerManager.destroySynchronizer(SYNCHRONIZER_ID);
  cornerstonetools.SynchronizerManager.destroy();
};

export const getVOISynchronizer = (SYNCHRONIZER_ID) => {
  let synchronizer =
    cornerstonetools.SynchronizerManager.getSynchronizer(SYNCHRONIZER_ID);

  if (!synchronizer) {
    synchronizer = createVOISynchronizer(SYNCHRONIZER_ID);
  }

  return synchronizer;
};

export const makeBytesAvailableInCache = () => {
  cornerstone.cache.decacheIfNecessaryUntilBytesAvailable(
    1024 * 1024 * 1024 * 0.5
  );
};

export const purgeCache = () => {
  cornerstone.cache.purgeCache();
};

export const purgeVolumeCache = () => {
  cornerstone.cache.purgeVolumeCache();
};

 

export const getMetadataTagValue = (metadataTag, imageId) => {
  try {
    return cornerstone.metaData.get(metadataTag, imageId);
  } catch (error) {
    console.error('Error fetching metadata:', error);
    return 'Error fetching metadata';
  }
};
